<template>
  <div>

  <modal-component v-model="modal.receiptDetails.active">
    <div slot="title">{{ modal.receiptDetails.title }}</div>
    <receipt-details
      v-if="modal.receiptDetails.paymentID"
      :paymentID="modal.receiptDetails.paymentID"
    ></receipt-details>
  </modal-component>

  <modal-component v-model="modal.paymentEditor.active" size="medium" title="Payment Details">
    <label v-if="hasCreditNotes" class="block font-bold my-4 select-none">
      <input
        class="mr-2 leading-tight"
        type="checkbox"
        v-model="settleCreditNotes"
      >
      <span class="text-sm">Settle credit notes</span>
    </label>

    <div slot="title">Payment Details</div>
    <payment-editor
      v-model="modal.paymentEditor.data"
      :payment-type="paymentType"
      :active.sync="modal.paymentEditor.active"
      :amount="totalValue"
      @save="createPayment"
      :amount-input="false"
      :pay-amount-percentage="100"
      title=""
    ></payment-editor>
  </modal-component>


  <div class="box">

    <div class="flex flex-wrap items-center justify-between">
      <filtering-toggle-button :filtering="filtering" />

      <div class="flex">
        <button
          type="button"
          class="btn text-sm px-4 flex items-center mb-1"
          :class="selectedIDs.length ? 'btn-green' : 'bg-theme-300 light:bg-theme-600'"
          :disabled="!selectedIDs.length"
          @click="openPaymentEditor(totalValue)"
        >
          <i class="fas fa-check"></i>
          <span class="ml-2">Settle invoices</span>
        </button>

        <button class="btn w-10 h-10 flex items-center justify-center focus:outline-none hover:text-green-500 mb-1" @click="getInvoices">
          <i class="fas fa-sync-alt"></i>
        </button>
      </div>
    </div>

    <div class="mt-4 text-red-500 font-bold">
      <span
        v-if="hasCreditNotes"
        class="font-weight-bold"
        id="total-value"
        v-tooltip="{ content: `
          <strong>Invoices:</strong> ${$formatMoney(totalInvoicesValue)}<br>
          <strong>Credit Notes:</strong> ${$formatMoney(totalCreditNotesValue)}
        ` }"
      >
        Total: {{ $formatMoney(totalValue) }}
      </span>
      <template v-else>Total: {{$formatMoney(totalValue)}}</template>
    </div>

    <div class="flex flex-wrap">
      <div
        class="w-full lg:w-2/12 pr-2 pt-4 border-r border-theme-darker"
        v-show="filtering.active"
      >
        <settle-invoices-filters
          :filters.sync="filters"
          @updated="getInvoices"
          @has-changes="filtering.hasChanges = $event"
        />
      </div>

      <div class="w-full overflow-x-auto" :class="{ 'lg:w-10/12': filtering.active}">
        <p class="text-lg text-center my-12" v-if="!hasInvoices">No data to display</p>
        <div v-else>
          <table class="table table-striped">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    v-model="selectAll"
                    @change="selectAllClick"
                  >
                </th>
                <th>#</th>
                <th v-if="contactType === 'customer'">Job</th>
                <th>Type</th>
                <th>Total</th>
                <th>Remaining</th>
                <th>Created at</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(invoice, i) in invoices" :key="i">
                <td>
                  <input
                    v-if="Math.round(invoice.total.remaining) > 0"
                    type="checkbox"
                    :value="invoice.id"
                    v-model="selectedIDs"
                  >
                  <span v-else v-tooltip="{ content: `Must settle zero or negative invoices manually. Click to view invoice` }">
                    <a
                    class="hover:text-theme-darker text-red-500 text-base cursor-pointer p-2"
                    @click.stop="$router.push('/invoice/preview/' + invoice.id)"
                    ><i class="fas fa-question"></i></a>
                  </span>
                </td>
                <td class="font-bold">{{ invoice.id }}</td>
                <td v-if="contactType === 'customer'">
                  <div v-if="invoice.job && invoice.job.type && invoice.job.vehicle">
                    <p>#{{ invoice.job.id }} {{invoice.job.type.name}}</p>
                    <span class="text-sm">{{ invoice.job.vehicle.make }} {{ invoice.job.vehicle.model }}</span>
                  </div>
                  <div v-else> - </div>
                </td>
                <td>
                  <p
                    class="font-bold text-xs text-white py-1 px-1 rounded text-center uppercase"
                    :class="invoice.type === 'purchases' ? 'bg-red-500' : 'bg-green-500'"
                  >{{ invoice.type }}</p>
                </td>
                <td>
                  <span
                    class="font-weight-bold"
                    v-tooltip="{ content: `
                      <strong>Discount:</strong> ${$formatMoney(invoice.total.discount)}<br>
                      <strong>Tax:</strong> ${$formatMoney(invoice.total.tax)}<br>
                      <strong>Paid:</strong> ${$formatMoney(invoice.payments.map(e => e.value).reduce((a, b) => a + b, 0))}<br>
                      <strong>Credit notes:</strong> ${$formatMoney( invoice.credit_notes.map(e => e.total.remaining).reduce((a, b) => a + b, 0) )}
                    ` }"
                  >{{ $formatMoney(invoice.total.sub - invoice.total.discount + invoice.total.tax) }}</span>
                </td>
                <td>
                  <span class="font-bold text-red-500">
                    {{ $formatMoney(invoice.total.remaining) }}
                  </span>
                </td>
                <td class="text-sm">{{ $moment(invoice.created_at).format(config.dateTimeFormats.display) }}</td>
                <td>
                  <div class="flex">
                    <a
                    class="hover:text-theme-darker text-base cursor-pointer p-2"
                    @click.stop="$router.push('/invoice/preview/' + invoice.id)"
                    ><i class="fas fa-info"></i></a>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>

  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
const ReceiptDetails = resolve => require(['@/components/pages/receipts/Details'], resolve)
const PaymentEditor = resolve => require(['@/components/pages/payments/Editor'], resolve)
const FilteringToggleButton = resolve => require(['@/components/filters/FilteringToggleButton'], resolve)
const SettleInvoicesFilters = resolve => require(['@/components/filters/Filters'], resolve)
const ModalComponent = resolve => require(['@/components/Modal'], resolve)

export default {
  props: {
    contactID: {},
    contactType: String,
    paymentType: String,
  },
  components: {
    FilteringToggleButton,
    ModalComponent,
    ReceiptDetails,
    PaymentEditor,
    SettleInvoicesFilters,
  },
  data() {
    return {
      invoices: [],
      payments: [],
      jobTypes: [],
      filtering: {
        active: false,
        hasChanges: false,
      },
      filters: {
        createdAt: {
          key: 'created_at',
          name: 'Created at date',
          active: true,
          flatpickr: {
            mode: 'range',
            static: false,
          },
          value: null,
        },
      },
      selectedIDs: [],
      selectAll: false,
      settleCreditNotes: true,
      modal: {
        receiptDetails: {
          active: false,
          title: '',
          paymentID: null,
        },
        paymentEditor: {
          active: false,
          data: {},
        }
      },
    }
  },
  watch: {
    selectedIDs() {
      this.selectAll = this.selectedIDs.length === this.invoices.length
    },
    invoices() {
      for (let i in this.invoices) {
        this.$setInvoiceTotals(this.invoices[i], true)
      }
    },
  },
  computed: {
    hasCreditNotes() {
      for(let i in this.selected) {
        if (this.selected[i].credit_notes && this.selected[i].credit_notes.length) {
          return true
        }
      }
      return false
    },
    selected() {
      return this.invoices.filter(invoice => this.selectedIDs.indexOf(invoice.id) !== -1)
    },
    totalValue() {
      return this.totalInvoicesValue - (this.settleCreditNotes ? this.totalCreditNotesValue : 0)
    },
    totalInvoicesValue() {
      let total = 0
      for(let i in this.selected) {
        total += this.calcTotalPayment(this.selected[i])
      }
      return total
    },
    totalCreditNotesValue() {
      let total = 0
      for(let i in this.selected) {
        if (this.selected[i].credit_notes && this.selected[i].credit_notes.length) {
          for (let j in this.selected[i].credit_notes) {
            total += this.calcTotalPayment(this.selected[i].credit_notes[j])
          }
        }
      }
      return total
    },
    hasInvoices() {
      return Object.entries(this.invoices).length > 0
    },
    ...mapGetters({
      config: 'config/config',
    })
  },
  mounted() {
    this.getInvoices()
  },
  methods: {
    openReceipt(paymentID) {
      this.modal.receiptDetails.paymentID = paymentID
      this.modal.receiptDetails.active = true
    },
    openPaymentEditor(total) {
      this.modal.paymentEditor.total = total
      this.modal.paymentEditor.active = true
    },
    calcTotalPayment(invoice) {
      let paidAmount = 0

      if(invoice.payments.length) {
        for (let i in invoice.payments) {
          paidAmount += invoice.payments[i].value
        }
      }

      return invoice.total.sub - invoice.total.discount + invoice.total.tax - paidAmount
    },
    selectAllClick() {
      this.selectedIDs = []
      if (this.selectAll) {
        for (let i in this.invoices) {
          if (Math.round(this.invoices[i].total.remaining) > 0) {
            this.selectedIDs.push(this.invoices[i].id)
          }
        }
      }
    },
    getJobTypes() {
      axios.get('job-types')
        .then((response) => {
          this.jobTypes = response.data
          for (let i in this.invoices) {
            const inv = this.invoices[i]
            if (inv.job) {
              for (let j in this.jobTypes) {
                if (this.jobTypes[j].id === inv.job.job_type_id) {
                  this.$set(inv.job, 'type', this.jobTypes[j])
                }
              }
            }
          }
        })
    },
    getInvoices() {
      let filters = this.$getFilterValues(this.filters)
      if (filters.created_at) {
        let createdAt = this.$getRangeDates(filters.created_at, this.config)
        filters.dateFrom = createdAt.from
        filters.dateTo = createdAt.to
      }
      filters.contact_id = this.contactID

      axios.get('mass-settle', {params: {
        filters,
      }})
        .then(response => {
          if (response.data) {
            this.getJobTypes()

            const res = response.data
            this.invoices = Object.assign([], res)
          }
        })
    },
    createPayment() {
      axios.post('mass-settle/settle', {
        paymentData: this.modal.paymentEditor.data,
        invoiceIDs: this.selectedIDs,
        contactId: this.contactID,
        settleCreditNotes: this.settleCreditNotes,
      })
        .then(response => {
          this.modal.paymentEditor.active = false
          this.getInvoices()
          this.selectAll = false
          this.selectedIDs = []
          this.openReceipt(response.data.id)
          this.$EventBus.$emit('mass-settle.settled')
        })
        .catch((error) => {
          console.log(error)
        })
    },
  }
}
</script>
